import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Typography,
  Space,
  Row,
  Col,
  Badge,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  AppstoreAddOutlined,
} from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title } = Typography;
const { Option } = Select;

const AddSalesChannel = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await masterService.addSalesChannel({
        ...values,
        sc_name: (values.sc_name || "").trim(),
      });

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Sales channel added successfully",
        });
        form.resetFields();
        navigate("/sales-channel");
      } else {
        notify.error({
          message: response.data.message || "Failed to add sales channel",
        });
      }
    } catch (error) {
      console.error("Error adding sales channel:", error);
      notify.error({ message: error?.data?.message || "Error adding sales channel" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/sales-channel");
  };

  useEffect(() => {
    setPageTitle("Add Sales Channel");
  }, [setPageTitle]);

  return (
    <div className="min-h-screen">
      <Card
      >
        <Form
          form={form}
          layout="vertical"
          size="large"
          onFinish={handleSubmit}
          initialValues={{
            sc_is_active: "Y",
          }}
          className="space-y-6"
        >
          {/* Channel Information Section */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <AppstoreAddOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Channel Information
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Channel Name
                    </span>
                  }
                  name="sc_name"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "Please enter channel name",
                    },
                    {
                      max: 255,
                      message: "Channel Name must be 255 characters or fewer",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter channel name"
                    className="rounded-lg"
                    maxLength={255}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Status
                    </span>
                  }
                  name="sc_is_active"
                >
                  <Select className="rounded-lg">
                    <Option value="Y">
                      <Badge status="success" text="Active" />
                    </Option>
                    <Option value="N">
                      <Badge status="default" text="Inactive" />
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Action Buttons */}
          <div
            className="flex justify-end gap-4 pt-6 border-t"
            style={{ borderColor: "#d9d9d9" }}
          >
            <Space size="middle">
              <Button
                onClick={handleReset}
                icon={<ReloadOutlined />}
                size="large"
                className="px-6"
              >
                Reset
              </Button>
              <Button
                onClick={handleGoBack}
                icon={<ArrowLeftOutlined />}
                size="large"
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                size="large"
                className="px-8"
              >
                {loading ? "Adding..." : "Add Channel"}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default AddSalesChannel;
